import React, { useState, useEffect } from 'react';

const pad = (n) => String(n).padStart(2, '0');

const Cell = ({ v, label }) => (
  <div className="flex flex-col items-center min-w-[52px]">
    <span className="font-bold tabular-nums text-white text-xl sm:text-2xl leading-none">{pad(v)}</span>
    <span className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{label}</span>
  </div>
);

/**
 * Live countdown to an event start time.
 * Renders a compact "Xd Yh Zm Ws" style block matching the brand aesthetic.
 * Auto-hides once the event has started.
 */
export default function EventCountdown({ targetISO, className = '', compact = false }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const target = new Date(targetISO).getTime();
  const diff = target - now;
  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 text-xs font-semibold text-white/90 ${className}`} data-testid="event-countdown-compact">
        <span className="tabular-nums">{days}d {pad(hours)}h {pad(minutes)}m</span>
        <span className="text-white/50">to go</span>
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 sm:gap-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 px-3 sm:px-4 py-2.5 ${className}`} data-testid="event-countdown">
      <Cell v={days} label="Days" />
      <span className="text-white/30 text-xl">:</span>
      <Cell v={hours} label="Hrs" />
      <span className="text-white/30 text-xl">:</span>
      <Cell v={minutes} label="Min" />
      <span className="text-white/30 text-xl">:</span>
      <Cell v={seconds} label="Sec" />
    </div>
  );
}
